<template>
  <div class="cashier-page min-h-screen bg-gradient-to-b from-slate-100 via-slate-50 to-slate-100">
    <main class="mx-auto w-full max-w-xl px-4 py-6 md:py-10">
      <!-- 顶部品牌栏 -->
      <header class="mb-5 flex items-center justify-between rounded-2xl border border-white/80 bg-white/80 px-5 py-4 shadow-sm backdrop-blur">
        <div class="flex items-center gap-3">
          <div class="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 shadow-md shadow-blue-500/30">
            <svg class="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <div class="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse"></div>
          </div>
          <div>
            <div class="text-[10px] font-semibold uppercase tracking-widest text-blue-600">Secure Checkout</div>
            <h1 class="text-lg font-bold text-slate-900">收银台</h1>
          </div>
        </div>
        <span v-if="!loading && !loadError" class="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold shadow-sm" :class="statusClass">
          <span v-if="statusCode === 0" class="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
          {{ statusLabel }}
        </span>
      </header>

      <!-- 加载状态 -->
      <section v-if="loading" class="flex flex-col items-center justify-center rounded-3xl border border-slate-200/60 bg-white p-12 shadow-xl">
        <div class="h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
        <p class="mt-4 text-sm text-slate-500">正在加载账单信息...</p>
      </section>

      <!-- 错误状态 -->
      <section v-else-if="loadError" class="rounded-3xl border border-red-200/60 bg-white p-10 text-center shadow-xl">
        <div class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <svg class="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 class="mt-4 text-lg font-semibold text-red-700">收银台加载失败</h2>
        <p class="mt-2 text-sm text-red-600">{{ loadError }}</p>
      </section>

      <!-- 收银台主卡片 -->
      <section v-else class="overflow-hidden rounded-3xl border border-slate-200/60 bg-white shadow-xl">
        <!-- 金额展示区 -->
        <div class="border-b border-slate-100 bg-gradient-to-b from-blue-50/80 via-white to-white px-6 pb-7 pt-8 text-center">
          <p class="truncate text-sm font-medium text-slate-500">{{ presentation.title || '账单支付' }}</p>
          <p v-if="payeeNameText" class="mt-1 truncate text-xs text-slate-500">收款方：{{ payeeNameText }}</p>
          <p class="mt-3 text-slate-900">
            <span class="mr-1 text-2xl font-bold">¥</span>
            <span class="text-5xl font-black tracking-tight">{{ amountNumberText }}</span>
          </p>
          <div class="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-400">
            <span>收银台号：{{ presentation.cashierNo || cashierNo }}</span>
          </div>
          <p class="mt-2 text-[11px] leading-relaxed text-slate-400">订单不会因超时自动关闭，若支付凭证失效可重新发起。</p>
        </div>

        <!-- 支付成功 -->
        <div v-if="statusCode === 2" class="flex flex-col items-center px-6 py-10 text-center">
          <div class="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
            <svg class="h-8 w-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="mt-4 text-lg font-bold text-emerald-700">支付成功</h3>
          <p class="mt-1 text-sm text-slate-500">订单已完成支付，请保留截图作为凭证。</p>
          <div class="mt-5 w-full space-y-2 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 text-left">
            <p class="flex items-center justify-between text-xs"><span class="text-emerald-700/70">支付金额</span><span class="font-semibold text-emerald-800">{{ currencyText(presentation.amount) }}</span></p>
            <p v-if="payeeNameText" class="flex items-start justify-between gap-3 text-xs">
              <span class="shrink-0 text-emerald-700/70">收款方</span>
              <span class="min-w-0 break-words text-right font-semibold text-emerald-800">{{ payeeNameText }}</span>
            </p>
            <p class="flex items-center justify-between text-xs"><span class="text-emerald-700/70">账单号</span><span class="font-mono font-medium text-emerald-800">{{ presentation.cashierNo || cashierNo }}</span></p>
            <p class="flex items-center justify-between text-xs"><span class="text-emerald-700/70">订单号</span><span class="font-mono font-medium text-emerald-800">{{ orderInfo.orderNo || '-' }}</span></p>
            <p class="flex items-center justify-between text-xs"><span class="text-emerald-700/70">支付方式</span><span class="font-medium text-emerald-800">{{ successMethodLabel }}</span></p>
            <p class="flex items-center justify-between text-xs"><span class="text-emerald-700/70">支付时间</span><span class="font-medium text-emerald-800">{{ datetimeText(presentation.paidTime) }}</span></p>
          </div>
        </div>

        <!-- 收银台已关闭 -->
        <div v-else-if="statusCode === 3" class="flex flex-col items-center px-6 py-10 text-center">
          <div class="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg class="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 class="mt-4 text-lg font-bold text-red-700">收银台已关闭</h3>
          <p class="mt-1 text-sm text-slate-500">请联系商家确认账单状态后再继续支付。</p>
        </div>

        <!-- 支付操作区 -->
        <template v-else>
          <div class="px-6 pt-6">
            <!-- 凭证失效提示 -->
            <div v-if="statusCode === 4" class="mb-5 flex items-start gap-2.5 rounded-xl border border-amber-200/70 bg-amber-50/80 p-3">
              <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p class="text-xs font-semibold text-amber-700">当前支付凭证已失效</p>
                <p class="mt-0.5 text-xs text-amber-600">{{ presentation.failureReason || '订单仍可继续支付，请重新发起当前支付方式获取新的二维码或网银跳转。' }}</p>
                <p v-if="presentation.voucherExpiresAt" class="mt-0.5 text-[11px] text-amber-500">原支付凭证失效时间：{{ datetimeText(presentation.voucherExpiresAt) }}</p>
              </div>
            </div>

            <!-- 支付方式切换 -->
            <div v-if="paymentMethodCards.length" class="grid gap-1 rounded-xl bg-slate-100 p-1" :style="{ gridTemplateColumns: `repeat(${paymentMethodCards.length}, minmax(0, 1fr))` }">
              <button
                v-for="card in paymentMethodCards"
                :key="card.key"
                type="button"
                class="flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
                :class="activeMethod === card.key ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'"
                :disabled="actionLoading"
                @click="activateMethod(card.key)"
              >
                <svg v-if="card.icon === 'scan'" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M4 7V5a1 1 0 011-1h2m10 0h2a1 1 0 011 1v2M4 17v2a1 1 0 001 1h2m10 0h2a1 1 0 001-1v-2M8 8h3v3H8V8zm5 0h3v3h-3V8zm-5 5h3v3H8v-3zm5 0h1.5m1.5 0v1.5M13 16h3" />
                </svg>
                <svg v-else-if="card.icon === 'ebank'" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
                {{ card.label }}
              </button>
            </div>
            <div v-else class="rounded-xl border border-dashed border-slate-300 bg-slate-50/70 px-4 py-6 text-center">
              <p class="text-sm font-medium text-slate-700">当前账单暂未开放可用支付方式</p>
              <p class="mt-2 text-xs text-slate-500">请联系商家确认账单状态或稍后重新进入收银台。</p>
            </div>
          </div>

          <!-- 扫码支付 -->
          <div v-if="currentScanMethod && allowScan" class="flex flex-col items-center px-6 py-6">
            <!-- 二维码 -->
            <div class="relative overflow-hidden rounded-2xl border-2 transition-colors" :class="scanExpired ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 bg-white shadow-sm'">
              <div v-if="scanData && scanQrImage" class="p-4">
                <div class="relative h-56 w-56">
                  <img :src="scanQrImage" alt="支付二维码" class="h-full w-full rounded-lg object-contain">
                  <!-- 二维码过期遮罩 -->
                  <div v-if="scanExpired" class="absolute inset-0 flex flex-col items-center justify-center rounded-lg bg-slate-900/85 backdrop-blur-sm">
                    <svg class="h-10 w-10 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p class="mt-2 text-sm font-medium text-white">二维码已过期</p>
                  </div>
                </div>
              </div>
              <div v-else class="flex h-64 w-64 flex-col items-center justify-center p-6 text-center">
                <svg class="h-12 w-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                <p class="mt-3 text-xs text-slate-400">{{ actionLoading ? '正在生成支付二维码...' : (currentScanMethod.hint || '点击下方按钮生成支付二维码') }}</p>
              </div>
            </div>

            <!-- 支持渠道 -->
            <div v-if="scanData" class="mt-4 flex items-center gap-2">
              <span class="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
                <span class="flex h-4 w-4 items-center justify-center rounded bg-[#1677ff] text-[10px] font-bold leading-none text-white">支</span>
                支付宝
              </span>
              <span class="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600">
                <svg class="h-3.5 w-3.5 text-[#07c160]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 01.213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 00.167-.054l1.903-1.114a.864.864 0 01.717-.098 10.16 10.16 0 002.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178A1.17 1.17 0 014.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178 1.17 1.17 0 01-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 01.598.082l1.584.926a.272.272 0 00.14.045c.134 0 .24-.111.24-.247 0-.06-.024-.12-.04-.177l-.327-1.233a.582.582 0 01-.023-.156.49.49 0 01.201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.87c-.135-.004-.272-.012-.407-.012zm-1.54 3.576c.536 0 .97.44.97.983a.976.976 0 01-.97.983.976.976 0 01-.97-.983c0-.542.434-.983.97-.983zm4.857 0c.536 0 .97.44.97.983a.976.976 0 01-.97.983.976.976 0 01-.97-.983c0-.542.434-.983.97-.983z"/>
                </svg>
                微信
              </span>
            </div>
            <p v-if="scanData" class="mt-2 text-xs text-slate-400">打开支付宝或微信「扫一扫」完成支付</p>
            <p
              v-if="scanData && presentation.voucherExpiresAt"
              class="mt-1 text-[11px]"
              :class="scanExpired ? 'text-amber-600' : 'text-slate-400'"
            >二维码有效期至：{{ datetimeText(presentation.voucherExpiresAt) }}</p>

            <!-- 操作按钮 -->
            <div class="mt-5 w-full space-y-2">
              <button v-if="!scanData" class="action-btn" :disabled="actionLoading || isTerminal || !allowScan" @click="createScanPay">
                <svg v-if="!actionLoading" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                <span v-if="actionLoading" class="animate-spin">
                  <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
                {{ actionLoading ? '生成中...' : currentScanMethod.actionLabel }}
              </button>

              <button v-else-if="scanExpired" class="action-btn" :disabled="actionLoading || isTerminal" @click="refreshCurrentScan">
                <svg v-if="!actionLoading" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span v-if="actionLoading" class="animate-spin">
                  <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
                {{ actionLoading ? '刷新中...' : '刷新二维码' }}
              </button>
            </div>
          </div>

          <!-- 网银支付 -->
          <div v-if="activeMethod === 'EBANK' && allowEbank" class="space-y-5 px-6 py-6">
            <!-- 银行选择 -->
            <div>
              <div class="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                <p class="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                  <svg class="h-4 w-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  付款银行
                </p>
                <span class="text-xs text-slate-400">仅支持企业网银（公户）支付</span>
              </div>
              <div class="grid max-h-72 grid-cols-3 gap-2 overflow-y-auto pr-1 sm:grid-cols-4">
                <button
                  v-for="bank in ebankOptions"
                  :key="bank.value"
                  type="button"
                  class="relative flex flex-col items-center rounded-xl border-2 px-2 py-3 transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-60"
                  :class="selectedEbankBankCode === bank.value
                    ? 'border-blue-500 bg-blue-50/60 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm'"
                  :disabled="actionLoading || isTerminal"
                  @click="selectedEbankBankCode = bank.value"
                >
                  <span
                    class="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full ring-1 ring-slate-200/80"
                    :style="bankLogoFailed[bank.value] ? { backgroundColor: bank.color } : { backgroundColor: '#fff' }"
                  >
                    <img
                      v-if="!bankLogoFailed[bank.value]"
                      :src="bank.logo"
                      :alt="bank.label"
                      class="h-full w-full object-contain"
                      @error="markBankLogoFailed(bank.value)"
                    >
                    <span v-else class="text-xs font-bold text-white">{{ bank.short || bank.label.charAt(0) }}</span>
                  </span>
                  <span class="mt-1.5 text-xs font-medium leading-tight" :class="selectedEbankBankCode === bank.value ? 'text-blue-700' : 'text-slate-700'">{{ bank.label }}</span>
                  <span class="mt-0.5 font-mono text-[10px] text-slate-400">{{ bank.value }}</span>
                  <svg v-if="selectedEbankBankCode === bank.value" class="absolute right-1 top-1 h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                    <path fill-rule="evenodd" d="M12 2a10 10 0 100 20 10 10 0 000-20zm4.707 7.707a1 1 0 00-1.414-1.414L11 12.586l-1.793-1.793a1 1 0 00-1.414 1.414l2.5 2.5a1 1 0 001.414 0l5-5z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- 提示信息 -->
            <div class="flex items-start gap-2.5 rounded-xl border border-amber-200/60 bg-amber-50/80 p-3">
              <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p class="text-xs text-amber-700">发起后将跳转到银行页面完成支付，请勿关闭当前收银台页面。</p>
            </div>

            <!-- 支付按钮 -->
            <button class="action-btn" :disabled="actionLoading || isTerminal || !allowEbank || !selectedEbankBankCode" @click="createEbankPay">
              <svg v-if="!actionLoading" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span v-if="actionLoading" class="animate-spin">
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
              {{ actionLoading ? '处理中...' : (selectedEbankBank ? `前往 ${selectedEbankBank.label} 支付` : '请先选择付款银行') }}
            </button>
          </div>
        </template>

        <!-- 订单信息 -->
        <details class="group border-t border-slate-100">
          <summary class="flex cursor-pointer list-none items-center justify-between px-6 py-4 text-sm font-semibold text-slate-600 transition hover:text-slate-900">
            <span class="flex items-center gap-2">
              <svg class="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              订单信息
            </span>
            <svg class="h-4 w-4 text-slate-400 transition-transform duration-200 group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          <div class="grid gap-3 px-6 pb-6 sm:grid-cols-2">
            <div v-if="payeeNameText" class="rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5 sm:col-span-2">
              <p class="text-xs text-slate-500">收款方</p>
              <p class="mt-1 break-words text-sm font-semibold text-slate-900">{{ payeeNameText }}</p>
            </div>
            <div class="rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5">
              <p class="text-xs text-slate-500">订单编号</p>
              <p class="mt-1 break-all font-mono text-sm font-semibold text-slate-900">{{ orderInfo.orderNo || '-' }}</p>
            </div>
            <div class="rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5">
              <p class="text-xs text-slate-500">订单类型</p>
              <p class="mt-1 text-sm font-semibold text-slate-900">{{ orderInfo.orderType || '-' }}</p>
            </div>
            <div class="rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5">
              <p class="text-xs text-slate-500">下单时间</p>
              <p class="mt-1 text-sm font-semibold text-slate-700">{{ datetimeText(orderInfo.orderCreateTime) }}</p>
            </div>
            <div class="rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5">
              <p class="text-xs text-slate-500">购买人</p>
              <p class="mt-1 text-sm font-semibold text-slate-900">
                {{ orderInfo.customerName || '-' }}<span v-if="orderInfo.customerPhoneMasked" class="text-slate-500"> · {{ orderInfo.customerPhoneMasked }}</span>
              </p>
            </div>
            <div class="rounded-xl border border-dashed border-slate-300/80 bg-slate-50/50 p-3.5 sm:col-span-2">
              <p class="text-xs text-slate-500">商品/服务摘要</p>
              <p class="mt-1 text-sm font-medium text-slate-800">{{ orderInfo.productSummary || orderInfo.orderTitle || '暂无' }}</p>
            </div>
          </div>
        </details>
      </section>

      <!-- 底部安全提示 -->
      <p class="mt-6 text-center text-[11px] text-slate-400">支付过程全程加密，请核对账单信息后再付款</p>
    </main>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import QRCode from 'qrcode'
import {
  createCashierEbank,
  createCashierScan,
  getCashierPresentation,
  getCashierStatus,
  resolveBackendPageUrl
} from '@/api/cashier'
import logoCCB from '@/assets/bank-icon/建设银行.png'
import logoABC from '@/assets/bank-icon/农业银行-05.png'
import logoBOC from '@/assets/bank-icon/中国银行.png'
import logoPSBC from '@/assets/bank-icon/邮政银行-02.png'
import logoICBC from '@/assets/bank-icon/工商银行.png'
import logoBOCOM from '@/assets/bank-icon/交通银行.png'
import logoSPDB from '@/assets/bank-icon/浦发银行.png'
import logoPINGAN from '@/assets/bank-icon/平安银行.png'
import logoCMBC from '@/assets/bank-icon/中国民生银行.png'
import logoGDB from '@/assets/bank-icon/广发银行.png'
import logoCZB from '@/assets/bank-icon/浙商银行.png'
import logoCEB from '@/assets/bank-icon/光大银行.png'
import logoCMB from '@/assets/bank-icon/招商银行.png'
import logoCITIC from '@/assets/bank-icon/中信银行.png'
import logoBOB from '@/assets/bank-icon/北京银行.png'
import logoHXB from '@/assets/bank-icon/华夏银行.png'
import logoBOSC from '@/assets/bank-icon/上海银行.png'
import logoCIB from '@/assets/bank-icon/兴业银行.png'
import logoSRCB from '@/assets/bank-icon/上海农商行.png'
import logoNBCB from '@/assets/bank-icon/宁波银行.png'
import logoBQD from '@/assets/bank-icon/青岛银行.png'
import logoZJRC from '@/assets/bank-icon/浙江农信.png'
import logoGLB from '@/assets/bank-icon/桂林银行.png'
import logoBCS from '@/assets/bank-icon/长沙银行.png'
import logoHZCB from '@/assets/bank-icon/杭州银行.png'
import logoLZCB from '@/assets/bank-icon/yh_logo_public_lzyh.png'
import logoYZRCB from '@/assets/bank-icon/鄞州银行.png'

const route = useRoute()
const cashierNo = computed(() => route.params.cashierNo)
const isMobileClient = /android|iphone|ipad|mobile/i.test(navigator.userAgent || '')
const clientType = isMobileClient ? 'MOBILE' : 'PC'

const loading = ref(true)
const loadError = ref('')
const actionLoading = ref(false)
const presentation = ref({})
const orderInfo = ref({})
const activeMethod = ref('SCAN_QR')
const statusCode = ref(0)
const attemptNo = ref('')
const pollTimer = ref(null)
// 默认不选中银行，由用户自行选择
const selectedEbankBankCode = ref('')
// 网银仅支持企业网银（公户）支付，账户类型固定为 CORPORATE
const EBANK_ACCOUNT_TYPE = 'CORPORATE'
const ebankOptions = [
  { label: '建设银行', value: 'CCB', color: '#0066b3', logo: logoCCB },
  { label: '农业银行', value: 'ABC', color: '#009a44', logo: logoABC },
  { label: '中国银行', value: 'BOC', color: '#a71e32', logo: logoBOC },
  { label: '邮储银行', value: 'PSBC', color: '#007a3d', logo: logoPSBC },
  { label: '工商银行', value: 'ICBC', color: '#c7000b', logo: logoICBC },
  { label: '交通银行', value: 'BOCOM', color: '#003c7e', logo: logoBOCOM },
  { label: '浦发银行', value: 'SPDB', color: '#1b3a8c', logo: logoSPDB },
  { label: '平安银行', value: 'PINGAN', color: '#ff6a00', logo: logoPINGAN },
  { label: '民生银行', value: 'CMBC', color: '#0085d0', logo: logoCMBC },
  { label: '广发银行', value: 'GDB', color: '#c60c30', logo: logoGDB },
  { label: '浙商银行', value: 'CZB', color: '#d7000f', logo: logoCZB },
  { label: '光大银行', value: 'CEB', color: '#6f2c91', logo: logoCEB },
  { label: '招商银行', value: 'CMB', color: '#d71920', logo: logoCMB },
  { label: '中信银行', value: 'CITIC', color: '#e31b23', logo: logoCITIC },
  { label: '北京银行', value: 'BOB', color: '#d6000f', logo: logoBOB },
  { label: '华夏银行', value: 'HXB', color: '#c7161d', logo: logoHXB },
  { label: '上海银行', value: 'BOSC', color: '#003c71', logo: logoBOSC },
  { label: '兴业银行', value: 'CIB', color: '#004c97', logo: logoCIB },
  { label: '上海农商行', value: 'SRCB', color: '#0068b7', short: '沪农', logo: logoSRCB },
  { label: '宁波银行', value: 'NBCB', color: '#f08300', logo: logoNBCB },
  { label: '青岛银行', value: 'BQD', color: '#0064b5', logo: logoBQD },
  { label: '浙江农信', value: 'ZJRC', color: '#009944', short: '浙农', logo: logoZJRC },
  { label: '桂林银行', value: 'GLB', color: '#008cd6', logo: logoGLB },
  { label: '长沙银行', value: 'BCS', color: '#004b8d', logo: logoBCS },
  { label: '杭州银行', value: 'HZCB', color: '#0068b6', logo: logoHZCB },
  { label: '柳州银行', value: 'LZCB', color: '#b71c1c', logo: logoLZCB },
  { label: '鄞州银行', value: 'YZRCB', color: '#e8890c', short: '鄞州', logo: logoYZRCB }
]
// 银行 logo 加载失败时回退为品牌色字母徽章
const bankLogoFailed = ref({})
const markBankLogoFailed = (bankCode) => {
  bankLogoFailed.value = { ...bankLogoFailed.value, [bankCode]: true }
}
const paymentMethodMeta = {
  SCAN_QR: {
    key: 'SCAN_QR',
    icon: 'scan',
    label: '扫码支付',
    description: '支付宝、微信均可扫码'
  },
  EBANK: {
    key: 'EBANK',
    icon: 'ebank',
    label: '网银支付',
    description: '跳转银行页面完成付款'
  }
}
const scanMethodMeta = {
  SCAN_QR: {
    icon: 'scan',
    label: '扫码支付',
    hint: '支付宝、微信均可扫码完成付款。',
    actionLabel: '生成支付二维码'
  }
}

const scanData = ref(null)
const ebankData = ref(null)
const allowedMethods = computed(() => {
  const raw = presentation.value?.allowedMethods
  if (!Array.isArray(raw) || raw.length === 0) {
    return ['SCAN_QR', 'EBANK']
  }
  return raw
})
const allowScan = computed(() => allowedMethods.value.includes('SCAN_QR'))
const allowEbank = computed(() => allowedMethods.value.includes('EBANK'))
const paymentMethodCards = computed(() => {
  const cards = []
  if (allowScan.value) {
    cards.push(paymentMethodMeta.SCAN_QR)
  }
  if (allowEbank.value) {
    cards.push(paymentMethodMeta.EBANK)
  }
  return cards
})
const availableActiveMethods = computed(() => paymentMethodCards.value.map(item => item.key))
const currentScanMethod = computed(() => scanMethodMeta[activeMethod.value] || null)
const selectedEbankBank = computed(() => {
  return ebankOptions.find((item) => item.value === selectedEbankBankCode.value) || null
})
const successMethodLabel = computed(() => {
  const method = presentation.value?.successMethod || presentation.value?.paymentMethod || ''
  if (method === 'SCAN_QR') return '扫码支付'
  if (method === 'EBANK') return '网银支付'
  if (method === 'BANK_TRANSFER') return '银行转账'
  return method || '-'
})

const statusLabel = computed(() => {
  const map = { 0: '待支付', 1: '支付中', 2: '支付成功', 3: '已关闭', 4: '支付凭证失效' }
  return map[statusCode.value] || '待支付'
})

const statusClass = computed(() => {
  const map = {
    0: 'bg-slate-100 text-slate-700',
    1: 'bg-amber-100 text-amber-700',
    2: 'bg-emerald-100 text-emerald-700',
    3: 'bg-red-100 text-red-700',
    4: 'bg-amber-100 text-amber-700'
  }
  return map[statusCode.value] || map[0]
})

const isTerminal = computed(() => [2, 3].includes(statusCode.value))
const isScanMethod = (method) => method === 'SCAN_QR'
const scanExpired = computed(() => statusCode.value === 4 && !!scanData.value && isScanMethod(activeMethod.value))

const scanPayUrl = computed(() => {
  return scanData.value?.qrCode || ''
})
const scanQrImage = ref('')

const currencyText = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return '¥0.00'
  return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const amountNumberText = computed(() => {
  const n = Number(presentation.value?.amount)
  if (!Number.isFinite(n)) return '0.00'
  return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
})

const payeeNameText = computed(() => {
  const value = presentation.value?.payeeName
  return typeof value === 'string' ? value.trim() : ''
})

const datetimeText = (value) => {
  if (!value) return '-'
  return String(value).replace('T', ' ')
}

const loadPresentation = async () => {
  loading.value = true
  loadError.value = ''
  try {
    const data = await getCashierPresentation(cashierNo.value, clientType)
    presentation.value = data || {}
    orderInfo.value = data?.orderInfo || {}
    statusCode.value = Number(data?.status ?? 0)
    ensureActiveMethodAvailable()
  } catch (error) {
    loadError.value = error?.message || '无法加载收银台信息'
  } finally {
    loading.value = false
  }
}

const activateMethod = (method) => {
  if (!availableActiveMethods.value.includes(method)) return
  activeMethod.value = method
}

const ensureActiveMethodAvailable = () => {
  if (availableActiveMethods.value.includes(activeMethod.value)) {
    return
  }
  activeMethod.value = availableActiveMethods.value[0] || ''
}

const clearMethodPayloads = () => {
  scanData.value = null
  ebankData.value = null
}

const startPolling = () => {
  stopPolling()
  if (!attemptNo.value || isTerminal.value) return
  pollTimer.value = setInterval(async () => {
    try {
      const data = await getCashierStatus(cashierNo.value, attemptNo.value)
      statusCode.value = Number(data?.status ?? statusCode.value)
      presentation.value = {
        ...presentation.value,
        failureReason: data?.failureReason || presentation.value?.failureReason || '',
        voucherExpiresAt: data?.voucherExpiresAt || presentation.value?.voucherExpiresAt || '',
        attemptStatus: data?.attemptStatus || presentation.value?.attemptStatus || '',
        orderStatus: data?.orderStatus || presentation.value?.orderStatus || ''
      }
      if ([2, 3, 4].includes(statusCode.value)) {
        stopPolling()
      }
    } catch (error) {
      // keep polling silently
    }
  }, 3000)
}

const stopPolling = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value)
    pollTimer.value = null
  }
}

const createScanPay = async () => {
  if (isTerminal.value || !allowScan.value || !isScanMethod(activeMethod.value)) return
  actionLoading.value = true
  clearMethodPayloads()
  try {
    const data = await createCashierScan(cashierNo.value, {
      clientType
    })
    scanData.value = data
    attemptNo.value = data?.attemptNo || ''
    statusCode.value = 1
    presentation.value = {
      ...presentation.value,
      voucherExpiresAt: data?.voucherExpiresAt || data?.expiresAt || ''
    }
    startPolling()
  } catch (error) {
    ElMessage.error(error?.message || `${currentScanMethod.value?.label || '扫码支付'}失败`)
  } finally {
    actionLoading.value = false
  }
}

const refreshCurrentScan = async () => {
  if (!isScanMethod(activeMethod.value)) return
  await createScanPay()
}

const autoCreateDefaultScan = async () => {
  if (loading.value || loadError.value || isTerminal.value || !allowScan.value || !isScanMethod(activeMethod.value)) return
  if (scanData.value && !scanExpired.value) return
  await createScanPay()
}

const redirectToEbankPayment = (paymentUrl, openedWindow) => {
  const redirectUrl = resolveBackendPageUrl(paymentUrl)
  if (!redirectUrl) {
    if (openedWindow && !openedWindow.closed) {
      openedWindow.close()
    }
    throw new Error('网银支付跳转地址缺失')
  }

  if (openedWindow && !openedWindow.closed) {
    try {
      openedWindow.opener = null
    } catch (error) {
      // ignore browser restrictions
    }
    openedWindow.location.href = redirectUrl
    return
  }

  window.location.assign(redirectUrl)
}

const createEbankPay = async () => {
  if (isTerminal.value || !allowEbank.value || !selectedEbankBankCode.value) return

  let bankWindow = null
  if (!isMobileClient) {
    bankWindow = window.open('', '_blank')
    if (bankWindow) {
      bankWindow.document.write(`
        <!DOCTYPE html>
        <html lang="zh-CN">
        <head>
          <meta charset="UTF-8">
          <title>网银支付跳转中</title>
        </head>
        <body style="font-family: sans-serif; text-align: center; padding-top: 48px;">
          <p style="font-size: 18px; font-weight: 600;">正在准备银行支付页面</p>
          <p style="color: #64748b;">请稍候，不要关闭此窗口。</p>
        </body>
        </html>
      `)
    }
  }

  actionLoading.value = true
  clearMethodPayloads()
  try {
    const data = await createCashierEbank(cashierNo.value, {
      bankCode: selectedEbankBankCode.value,
      accountType: EBANK_ACCOUNT_TYPE,
      clientType
    })
    ebankData.value = data
    attemptNo.value = data?.attemptNo || ''
    statusCode.value = 1
    startPolling()
    redirectToEbankPayment(data?.paymentUrl, bankWindow)
    ElMessage.success('网银支付页面已打开，请在银行页面完成支付')
  } catch (error) {
    if (bankWindow && !bankWindow.closed) {
      bankWindow.close()
    }
    ElMessage.error(error?.message || '发起网银支付失败')
  } finally {
    actionLoading.value = false
  }
}

watch(
  scanPayUrl,
  async (raw) => {
    if (!raw) {
      scanQrImage.value = ''
      return
    }
    try {
      scanQrImage.value = await QRCode.toDataURL(raw, {
        width: 320,
        margin: 1,
        errorCorrectionLevel: 'M',
        color: {
          dark: '#0f172a',
          light: '#ffffff'
        }
      })
    } catch (error) {
      scanQrImage.value = ''
      ElMessage.warning('二维码生成失败，请刷新后重试')
    }
  },
  { immediate: true }
)

watch(availableActiveMethods, ensureActiveMethodAvailable, { immediate: true })

watch(activeMethod, async (nextMethod, previousMethod) => {
  if (loading.value || nextMethod === previousMethod) {
    return
  }
  if (!isScanMethod(nextMethod)) {
    return
  }
  await createScanPay()
})

onMounted(async () => {
  await loadPresentation()
  await autoCreateDefaultScan()
})
onBeforeUnmount(stopPolling)
</script>

<style scoped>
.action-btn {
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 0.875rem;
  border: none;
  padding: 0.875rem 1.25rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #fff;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #0ea5e9 100%);
  box-shadow: 0 4px 14px rgb(37 99 235 / 0.4), 0 0 0 1px rgb(37 99 235 / 0.1);
}
.action-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgb(37 99 235 / 0.45), 0 0 0 1px rgb(37 99 235 / 0.15);
}
.action-btn:active:not(:disabled) {
  transform: translateY(0);
}
.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
details > summary::-webkit-details-marker {
  display: none;
}
</style>
